import { AnyAction, Middleware, ThunkAction, ThunkDispatch } from '@reduxjs/toolkit'

import { baseApi } from '../baseApi'

type ApiState = { [baseApi.reducerPath]: ReturnType<typeof baseApi.reducer> }

type ForceRefetchableEndpoint = {
  initiate(
    arg: unknown,
    options: { subscribe: false; forceRefetch: true },
  ): ThunkAction<unknown, ApiState, unknown, AnyAction>
}

type QueryThunkMeta = {
  condition?: boolean
  arg?: { subscribe?: boolean; endpointName: string; originalArgs: unknown; queryCacheKey: string }
}

/**
 * Hydrated queries keep their persisted fulfilledTimeStamp, so `refetchOnMountOrArgChange` skips them when they
 * subscribe within 20 minutes of the last fetch. `persistedApiReducer` marks them with `startedTimeStamp: -1`
 * until a request starts; revalidate them once on the first subscription that RTK Query skips.
 */
export const revalidateHydratedQueries: Middleware<{}, ApiState, ThunkDispatch<ApiState, unknown, AnyAction>> =
  (store) => (next) => (action: AnyAction) => {
    const result = next(action)
    const meta: QueryThunkMeta | undefined = action.meta
    const arg = meta?.arg
    if (action.type === `${baseApi.reducerPath}/executeQuery/rejected` && meta?.condition && arg?.subscribe) {
      const query = store.getState()[baseApi.reducerPath]?.queries[arg.queryCacheKey]
      // Endpoints are injected at runtime, so the base API's endpoint map has no static keys
      const endpoint = (baseApi.endpoints as Record<string, ForceRefetchableEndpoint | undefined>)[arg.endpointName]
      if (query?.startedTimeStamp === -1 && endpoint) {
        store.dispatch(endpoint.initiate(arg.originalArgs, { subscribe: false, forceRefetch: true }))
      }
    }
    return result
  }
